/**
 * Normaliza erros vindos do interceptor de Services/api.ts
 * ({ status, message, details }) ou de falhas de rede do axios.
 */
export interface ParsedApiError {
  status?: number;
  /** Mensagem pronta para o usuário. */
  message: string;
  /** Erros de validação por campo (chaves da API, ex.: "email", "addresses.0.city"). */
  fieldErrors: Record<string, string>;
  /** Sem resposta, timeout ou erro 5xx: a operação pode ter sido concluída no servidor. */
  uncertain: boolean;
}

const FRIENDLY_BY_STATUS: Record<number, string> = {
  401: 'Sua sessão expirou. Entre novamente para continuar.',
  403: 'Você não tem permissão para realizar esta ação.',
  404: 'Cadastro não encontrado. Ele pode ter sido excluído por outro usuário.',
  409: 'Já existe um cadastro com estes dados.',
  413: 'Os dados enviados são grandes demais.',
  429: 'Muitas tentativas em sequência. Aguarde alguns segundos e tente de novo.',
};

/** Mensagens técnicas que não devem chegar ao usuário. */
const looksTechnical = (message: string) =>
  /exception|sqlstate|no query results|stack|undefined|null given|\\|\.php/i.test(message);

export function parseApiError(error: any): ParsedApiError {
  const status: number | undefined =
    typeof error?.status === 'number' ? error.status : error?.response?.status;

  const details = error?.details ?? error?.response?.data?.errors;
  const fieldErrors: Record<string, string> = {};

  if (details && typeof details === 'object') {
    Object.entries(details).forEach(([key, value]) => {
      const first = Array.isArray(value) ? value[0] : value;
      if (typeof first === 'string' && first) fieldErrors[key] = first;
    });
  }

  if (!status) {
    const timeout = error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message ?? '');
    return {
      message: timeout
        ? 'O servidor demorou para responder.'
        : 'Não foi possível conectar ao servidor. Verifique sua internet.',
      fieldErrors,
      uncertain: true,
    };
  }

  const rawMessage: string =
    (typeof error?.message === 'string' && error.message) ||
    error?.response?.data?.message ||
    '';

  if (status >= 500) {
    return {
      status,
      message: 'O servidor encontrou um erro inesperado.',
      fieldErrors,
      uncertain: true,
    };
  }

  const firstFieldError = Object.values(fieldErrors)[0];
  const message =
    FRIENDLY_BY_STATUS[status] ??
    (firstFieldError ||
      (rawMessage && !looksTechnical(rawMessage) ? rawMessage : '') ||
      'Não foi possível salvar. Revise os dados e tente novamente.');

  return { status, message, fieldErrors, uncertain: false };
}
